import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
	Alert,
	AlertColor,
	Backdrop,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IPaymentTitularDLocal, ITransactionInGatewayDLocalRequest, PaymentTitularFormFieldLength } from '../../types';
import { checkShowErrorMessage } from '../../../common/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import makeStyles from '@mui/styles/makeStyles';
import { createDLocalTransaction } from 'features/payment/services';
import { FormFieldsMaxLength, ILocalize } from 'features/common/types';
import { emailPattern } from 'features/common/constants';
import { OrderDataToClient } from 'features/payment/types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { DLocalTitularDefaultValues } from 'features/transaction-details/helpers/buildDLocalTitularDefaults';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

export interface DLocalTitularFormProps {
	orderData: OrderDataToClient;
	clientLocation: ILocalize | null;
	countryCode: string;
	defaultValues: DLocalTitularDefaultValues;
	redirectTarget?: 'same_tab' | 'new_tab';
	submitLabel?: string;
	showOrderSummary?: boolean;
	submitDisabled?: boolean;
	onRedirectStarted?: () => void;
}

export const DLocalTitularForm = ({
	orderData,
	clientLocation,
	countryCode,
	defaultValues,
	redirectTarget = 'same_tab',
	submitLabel = 'Procesar',
	showOrderSummary = true,
	submitDisabled = false,
	onRedirectStarted,
}: DLocalTitularFormProps): JSX.Element => {
	const classes = useStyles();
	const {
		control,
		formState: { errors, isValid },
		getValues,
		reset,
	} = useForm({
		mode: 'all',
		defaultValues,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = React.useState<ShowAlertState>(defaultAlertState);

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues, clientLocation, reset]);

	const onSubmit = async (data: DLocalTitularDefaultValues): Promise<void> => {
		try {
			setLoading(true);
			const customerInfoFromForm: IPaymentTitularDLocal = {
				firstName: data.firstName,
				lastName: data.lastName,
				email: data.email,
				phone: data.phone,
				documentType: data.documentType,
				document: data.document,
				state: data.state,
				city: data.city,
				address: data.address,
				zipCode: data.zipCode,
			};
			const dataToSend: ITransactionInGatewayDLocalRequest = {
				paymentTitularDLocal: customerInfoFromForm,
				orderId: orderData.orderId,
			};
			const result = await createDLocalTransaction(dataToSend);
			onRedirectStarted?.();

			if (redirectTarget === 'new_tab') {
				window.open(result.data.redirectUrl, '_blank', 'noopener,noreferrer');
				setLoading(false);
				return;
			}

			window.location.href = result.data.redirectUrl;
		} catch (error: unknown) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al realizar la transacción'),
			});
			setLoading(false);
		}
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			{showOrderSummary && (
				<>
					<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
						Orden de pago por:
					</Typography>
					<Typography variant="h6" gutterBottom>
						{orderData.coinCode} {orderData.amount}
					</Typography>
				</>
			)}
			<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
				Datos del titular de pago
			</Typography>
			<form noValidate>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<Controller
							name="firstName"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.FIRSTNAME,
									message: `Máximo ${PaymentTitularFormFieldLength.FIRSTNAME} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Nombre"
									helperText={checkShowErrorMessage(Boolean(errors.firstName), errors.firstName?.message)}
									error={Boolean(errors.firstName)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="lastName"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.LASTNAME,
									message: `Máximo ${PaymentTitularFormFieldLength.LASTNAME} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Apellido"
									helperText={checkShowErrorMessage(Boolean(errors.lastName), errors.lastName?.message)}
									error={Boolean(errors.lastName)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="documentType"
							control={control}
							rules={{ required: { value: true, message: 'Este campo es requerido' } }}
							render={({ field }) => (
								<FormControl sx={{ width: '100%' }} required>
									<InputLabel id="document-type-label">Tipo de documento</InputLabel>
									<Select label="Tipo de documento" labelId="document-type-label" {...field}>
										{orderData.billingDocumentTypeToShow.map((document) => (
											<MenuItem key={document.id} value={document.id}>
												{document.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="document"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.DOCUMENT,
									message: `Máximo ${PaymentTitularFormFieldLength.DOCUMENT} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Documento de identidad"
									helperText={checkShowErrorMessage(Boolean(errors.document), errors.document?.message)}
									error={Boolean(errors.document)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="email"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								pattern: { value: emailPattern, message: 'Ingrese un email válido' },
								maxLength: {
									value: FormFieldsMaxLength.EMAIL,
									message: `Máximo ${FormFieldsMaxLength.EMAIL} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="E-Mail"
									helperText={checkShowErrorMessage(Boolean(errors.email), errors.email?.message)}
									error={Boolean(errors.email)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="phone"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: FormFieldsMaxLength.PHONE,
									message: `Máximo ${FormFieldsMaxLength.PHONE} caracteres`,
								},
							}}
							render={({ field }) => (
								<>
									<PhoneInput
										{...field}
										country={countryCode.toLowerCase()}
										enableSearch
										inputProps={{ required: true }}
										specialLabel="Teléfono *"
										inputStyle={{ width: '100%' }}
										onChange={(value) => field.onChange(value)}
									/>
									{errors.phone && <FormHelperText error>{errors.phone.message}</FormHelperText>}
								</>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="city"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.CITY,
									message: `Máximo ${PaymentTitularFormFieldLength.CITY} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Ciudad"
									helperText={checkShowErrorMessage(Boolean(errors.city), errors.city?.message)}
									error={Boolean(errors.city)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Controller
							name="state"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.STATE,
									message: `Máximo ${PaymentTitularFormFieldLength.STATE} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Estado"
									helperText={checkShowErrorMessage(Boolean(errors.state), errors.state?.message)}
									error={Boolean(errors.state)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={9}>
						<Controller
							name="address"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.ADDRESS,
									message: `Máximo ${PaymentTitularFormFieldLength.ADDRESS} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Dirección"
									helperText={checkShowErrorMessage(Boolean(errors.address), errors.address?.message)}
									error={Boolean(errors.address)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={3}>
						<Controller
							name="zipCode"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: PaymentTitularFormFieldLength.ZIPCODE,
									message: `Máximo ${PaymentTitularFormFieldLength.ZIPCODE} caracteres`,
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Código postal"
									helperText={checkShowErrorMessage(Boolean(errors.zipCode), errors.zipCode?.message)}
									error={Boolean(errors.zipCode)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						{submitDisabled && (
							<Alert severity="info" sx={{ mb: 1 }}>
								El checkout ya está abierto. Si necesitás otro intento, cerrá el modal y volvé a cobrar con DLocal
								(orden nueva).
							</Alert>
						)}
						<Button
							variant="contained"
							fullWidth
							startIcon={redirectTarget === 'new_tab' ? <OpenInNewIcon /> : undefined}
							onClick={() => onSubmit(getValues())}
							disabled={!isValid || loading || submitDisabled}
							color="success"
						>
							{submitLabel}
						</Button>
					</Grid>
				</Grid>
			</form>
			<Snackbar open={alert.show} autoHideDuration={2500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
